/**
 * LiveStrategyOptions Component
 * 
 * Displays the ranked strategy options (A–G) from the LiveStrategyCalculator
 * as a comparison table. Shows the best strategy highlighted plus all alternatives
 * with their time costs and descriptions.
 */

import React, { useMemo, useState } from "react";
import { LiveStrategyCalculator, LiveStrategyInput, LiveStrategyResult } from "../utils/liveStrategyCalculator";
import type { CarClass } from "../utils/liveStrategyCalculator";
import type { FuelSavingCalibration } from "../types/strategyCalibration";

interface LiveStrategyOptionsProps {
  avgLapTime: number | null;
  avgFuelPerLap: number | null;
  currentFuel: number | null;
  currentLap: number;
  raceDurationMinutes: number;
  fuelCapacity: number;
  isMultiClass?: boolean;
  fastestClassLapTime?: number;
  carClass?: CarClass;
  fuelSavingCalibration?: FuelSavingCalibration;
}

const TYPE_COLORS: Record<string, string> = {
  no_action:        'bg-green-500/20 text-green-400 border-green-500/40',
  save_only:        'bg-yellow-500/20 text-yellow-400 border-yellow-500/40',
  save_to_skip_stop:'bg-amber-500/20 text-amber-400 border-amber-500/40',
  splash_only:      'bg-blue-500/20 text-blue-400 border-blue-500/40',
  splash_and_save:  'bg-purple-500/20 text-purple-400 border-purple-500/40',
  full_stop:        'bg-red-500/20 text-red-400 border-red-500/40',
  mixed_pit:        'bg-cyan-500/20 text-cyan-400 border-cyan-500/40',
};

const TYPE_LABELS: Record<string, string> = {
  no_action:        'No Action',
  save_only:        'Save Only',
  save_to_skip_stop:'Save to Skip Stop',
  splash_only:      'Splash & Go',
  splash_and_save:  'Splash + Save',
  full_stop:        'Full Stop',
  mixed_pit:        'Mixed Pit',
};

const CAR_CLASSES: { value: CarClass | 'none'; label: string }[] = [
  { value: 'GTP', label: 'GTP (LMDh)' },
  { value: 'LMP2', label: 'LMP2' },
  { value: 'GT3', label: 'GT3' },
  { value: 'none', label: 'Custom / Unknown' },
];

export const LiveStrategyOptions: React.FC<LiveStrategyOptionsProps> = ({
  avgLapTime,
  avgFuelPerLap,
  currentFuel,
  currentLap,
  raceDurationMinutes,
  fuelCapacity,
  isMultiClass,
  fastestClassLapTime,
  carClass: initialCarClass,
  fuelSavingCalibration,
}) => {
  const [selectedClass, setSelectedClass] = useState<CarClass | 'none'>(initialCarClass || 'GTP');

  const calculator = useMemo(() => new LiveStrategyCalculator(), []);

  const result: LiveStrategyResult | null = useMemo(() => {
    if (!avgLapTime || !avgFuelPerLap || currentFuel === null || currentFuel === undefined) {
      return null;
    }

    const input: LiveStrategyInput = {
      currentLap,
      currentFuel,
      avgLapTime,
      avgFuelPerLap,
      raceDurationMinutes,
      fuelCapacity,
      isMultiClass: isMultiClass ?? false,
      fastestClassLapTime: fastestClassLapTime,
      carClass: selectedClass === 'none' ? undefined : selectedClass,
      fuelSavingCalibration,
    };

    return calculator.calculate(input);
  }, [calculator, avgLapTime, avgFuelPerLap, currentFuel, currentLap, raceDurationMinutes, fuelCapacity, isMultiClass, fastestClassLapTime, selectedClass, fuelSavingCalibration]);

  if (!result || result.status === 'insufficient_data') {
    return null;
  }

  const { bestStrategy, allStrategies } = result;

  return (
    <div className="bg-slate-800 p-6 rounded-xl border border-slate-700">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h2 className="text-xl font-semibold text-white">
            Live Strategy Options
          </h2>
          <p className="text-slate-400 text-sm mt-1">
            Real-time comparison of all viable strategies — cheapest wins
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="text-sm text-slate-400">Car Class:</label>
          <select
            value={selectedClass}
            onChange={(e) => setSelectedClass(e.target.value as CarClass | 'none')}
            className="bg-slate-700 border border-slate-600 rounded-lg px-3 py-1.5 text-sm text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {CAR_CLASSES.map(c => (
              <option key={c.value} value={c.value}>{c.label}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Race State Summary */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Laps Left</div>
          <div className="text-lg font-bold text-white">{result.lapsRemaining}</div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Fuel Laps</div>
          <div className={`text-lg font-bold ${result.currentFuelLaps >= result.lapsRemaining ? 'text-green-400' : 'text-red-400'}`}>
            {result.currentFuelLaps.toFixed(1)}
          </div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Fuel Deficit</div>
          <div className={`text-lg font-bold ${result.fuelDeficit <= 0 ? 'text-green-400' : 'text-red-400'}`}>
            {result.fuelDeficit <= 0 ? `+${Math.abs(result.fuelDeficit).toFixed(1)}L` : `-${result.fuelDeficit.toFixed(1)}L`}
          </div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Progress</div>
          <div className="text-lg font-bold text-white">{result.raceProgress.toFixed(0)}%</div>
        </div>
        <div className="bg-slate-900/50 rounded-lg p-3 text-center">
          <div className="text-slate-400 text-xs">Stops Left</div>
          <div className="text-lg font-bold text-white">{result.pitStopsRemaining}</div>
        </div>
      </div>

      {/* Best Strategy Highlight */}
      <div className="bg-green-900/30 border border-green-500/40 rounded-lg p-4 mb-5">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-green-400 text-lg font-bold">★ Recommended</span>
          <span className={`text-xs px-2 py-0.5 rounded-full border ${TYPE_COLORS[bestStrategy.type] || 'bg-slate-700 text-slate-300'}`}>
            {TYPE_LABELS[bestStrategy.type] || bestStrategy.type}
          </span>
        </div>
        <p className="text-white text-sm">{bestStrategy.description}</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-sm">
          <div>
            <div className="text-slate-400">Time Cost</div>
            <div className="text-lg font-bold text-white">{bestStrategy.totalTimeCost.toFixed(1)}s</div>
          </div>
          <div>
            <div className="text-slate-400">Pit Stops</div>
            <div className="text-lg font-bold text-white">{bestStrategy.pitStops}</div>
          </div>
          <div>
            <div className="text-slate-400">Fuel Save</div>
            <div className="text-lg font-bold text-white">
              {bestStrategy.fuelSavePercent > 0 ? `${bestStrategy.fuelSavePercent.toFixed(1)}%` : 'None'}
            </div>
          </div>
          <div>
            <div className="text-slate-400">Pit Type</div>
            <div className="text-lg font-bold text-white">
              {bestStrategy.pitType ? (bestStrategy.pitType === 'full' ? 'Full + Tires' : 'Splash') : '—'}
            </div>
          </div>
        </div>
      </div>

      {/* All Options Comparison */}
      {allStrategies.length > 1 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-700 text-slate-400">
                <th className="px-3 py-2 text-left">#</th>
                <th className="px-3 py-2 text-left">Option</th>
                <th className="px-3 py-2 text-right">Time Cost</th>
                <th className="px-3 py-2 text-right">Pit Cost</th>
                <th className="px-3 py-2 text-right">Save Cost</th>
                <th className="px-3 py-2 text-right">Stops</th>
                <th className="px-3 py-2 text-right">Save %</th>
                <th className="px-3 py-2 text-right">Pit Type</th>
                <th className="px-3 py-2 text-left">Action</th>
              </tr>
            </thead>
            <tbody>
              {allStrategies.map((opt, i) => {
                const isBest = i === 0;
                return (
                  <tr
                    key={i}
                    className={`
                      border-b border-slate-700/50 transition-colors
                      ${isBest ? 'bg-green-900/15' : 'hover:bg-slate-700/30'}
                    `}
                  >
                    <td className="px-3 py-2 font-mono">
                      {isBest ? '★' : i + 1}
                    </td>
                    <td className="px-3 py-2">
                      <span className={`text-xs px-2 py-0.5 rounded-full border ${TYPE_COLORS[opt.type] || 'bg-slate-700 text-slate-300'}`}>
                        {TYPE_LABELS[opt.type] || opt.type}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-bold text-white">
                      {opt.totalTimeCost.toFixed(1)}s
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-300">
                      {opt.pitTimeCost > 0 ? `${opt.pitTimeCost.toFixed(1)}s` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono text-slate-300">
                      {opt.fuelSaveTimeCost > 0 ? `${opt.fuelSaveTimeCost.toFixed(1)}s` : '—'}
                    </td>
                    <td className="px-3 py-2 text-right font-mono">{opt.pitStops}</td>
                    <td className="px-3 py-2 text-right font-mono">
                      {opt.fuelSavePercent > 0 ? (
                        <span className="text-yellow-400">{opt.fuelSavePercent.toFixed(1)}%</span>
                      ) : (
                        <span className="text-slate-500">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right text-xs">
                      {opt.pitType ? (
                        <span className={opt.pitType === 'full' ? 'text-green-400' : 'text-orange-400'}>
                          {opt.pitType === 'full' ? 'Full' : 'Splash'}
                        </span>
                      ) : '—'}
                    </td>
                    <td className="px-3 py-2 text-left text-xs text-slate-300 max-w-[250px] truncate" title={opt.description}>
                      {opt.description}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Status Message */}
      <div className={`mt-4 p-3 rounded-lg text-sm ${
        result.status === 'ok' ? 'bg-green-900/20 border border-green-500/30 text-green-300' :
        result.status === 'saving_required' ? 'bg-yellow-900/20 border border-yellow-500/30 text-yellow-300' :
        'bg-red-900/20 border border-red-500/30 text-red-300'
      }`}>
        {result.message}
      </div>

      {/* Legend */}
      <div className="mt-4 pt-4 border-t border-slate-700 flex flex-wrap gap-2 text-xs">
        {Object.entries(TYPE_LABELS).map(([key, label]) => (
          <span key={key} className={`px-2 py-0.5 rounded-full border ${TYPE_COLORS[key]}`}>
            {label}
          </span>
        ))}
      </div>
    </div>
  );
};
